import { useCallback, useEffect, useMemo, useState } from 'react';
import { HardDriveUpload, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadLimitsService } from '../../services/uploadLimitsService';
import { KINDS, newRow, slugify, toConfig, toRows, validate } from './uploadPlansModel';
import './admin-learning.css';
import './admin-uploads.css';

/**
 * Envío AP-2 · Admin › Subidas: tope por archivo de cada plan, plan por
 * defecto y subida directa al almacenamiento. El plan de cada usuario se
 * asigna en Usuarios › (usuario) › Plan.
 */
export default function AdminUploadsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [defaultPlan, setDefaultPlan] = useState('');
  const [direct, setDirect] = useState(true);
  const [platform, setPlatform] = useState({ ceiling_mb: 2048, multipart_max_mb: 50 });
  const [saved, setSaved] = useState('');

  const apply = (data) => {
    setRows(toRows(data.config));
    setDefaultPlan(data.config.default_plan);
    setDirect(data.config.direct_upload !== false);
    setPlatform({ ceiling_mb: data.ceiling_mb, multipart_max_mb: data.multipart_max_mb });
    setSaved(JSON.stringify(data.config));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { apply(await uploadLimitsService.getConfig()); } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar los planes de subida');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const config = useMemo(() => toConfig(rows, defaultPlan, direct), [rows, defaultPlan, direct]);
  const dirty = saved && JSON.stringify(config) !== saved;
  const errors = useMemo(() => validate(rows, defaultPlan, platform.ceiling_mb),
    [rows, defaultPlan, platform.ceiling_mb]);

  const update = (index, patch) => setRows((list) => list.map((r, i) => {
    if (i !== index) return r;
    const next = { ...r, ...patch };
    // En un plan nuevo el identificador sigue al nombre hasta que se guarda.
    if (r.isNew && 'label' in patch) {
      const slug = slugify(patch.label);
      if (slug) {
        if (defaultPlan === r.id) setDefaultPlan(slug);
        next.id = slug;
      }
    }
    return next;
  }));

  const remove = (index) => {
    const row = rows[index];
    setRows((list) => list.filter((_, i) => i !== index));
    if (row.id === defaultPlan) setDefaultPlan('');
  };

  const save = async () => {
    if (errors.length) { toast.error(errors[0]); return; }
    setSaving(true);
    try {
      apply(await uploadLimitsService.saveConfig(config));
      toast.success('Planes de subida guardados');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="sml-adl" data-testid="admin-uploads-panel">
      <div className="sml-adl-main">
        <header className="sml-adl-head">
          <div>
            <h3><HardDriveUpload size={18} aria-hidden="true" /> Planes de subida</h3>
            <p>Tamaño máximo por archivo en cada plan. El plan de cada usuario se asigna en
              Usuarios › (usuario) › Plan; quien no tiene uno usa el plan por defecto.</p>
          </div>
          <div className="sml-adl-head-actions">
            <button type="button" className="sml-adl-icon" onClick={load} aria-label="Actualizar" disabled={loading}>
              {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            </button>
            <button type="button" className="sml-adl-primary" onClick={save}
                    disabled={!dirty || saving} data-testid="admin-uploads-save">
              {saving && <Loader2 size={15} className="animate-spin" />} Guardar cambios
            </button>
          </div>
        </header>

        <table className="sml-adl-table sml-adu-table">
          <thead>
            <tr>
              <th>Plan</th>
              <th>Identificador</th>
              {KINDS.map((k) => <th key={k.key}>{k.label} (MB)</th>)}
              <th>Por defecto</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`admin-uploads-row-${r.id}`}>
                <td>
                  <input aria-label="Nombre del plan" value={r.label} placeholder="Ej.: Estudio"
                         maxLength={40} onChange={(e) => update(i, { label: e.target.value })} />
                </td>
                <td>
                  {r.isNew
                    ? <input aria-label="Identificador" value={r.id} maxLength={32}
                             onChange={(e) => update(i, { id: e.target.value.toLowerCase() })} />
                    : <code>{r.id}</code>}
                </td>
                {KINDS.map((k) => (
                  <td key={k.key}>
                    <input type="number" inputMode="numeric" min={1} max={platform.ceiling_mb}
                           aria-label={`${k.label} en MB para ${r.label || r.id}`}
                           className="sml-adu-num" value={r[k.key]}
                           onChange={(e) => update(i, { [k.key]: e.target.value })} />
                  </td>
                ))}
                <td>
                  <input type="radio" name="sml-adu-default" aria-label={`${r.label || r.id} por defecto`}
                         checked={defaultPlan === r.id} onChange={() => setDefaultPlan(r.id)} />
                </td>
                <td>
                  <button type="button" className="sml-adl-icon" onClick={() => remove(i)}
                          disabled={rows.length === 1} aria-label={`Borrar el plan ${r.label || r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div>
          <button type="button" className="sml-adl-ghost" onClick={() => setRows((l) => [...l, newRow(l)])}
                  disabled={rows.length >= 12} data-testid="admin-uploads-add">
            <Plus size={15} aria-hidden="true" /> Agregar plan
          </button>
        </div>

        {errors.length > 0 && dirty && (
          <ul className="sml-adu-errors" role="alert">
            {errors.map((e) => <li key={e}>{e}</li>)}
          </ul>
        )}

        <label className="sml-adu-switch">
          <input type="checkbox" checked={direct} onChange={(e) => setDirect(e.target.checked)}
                 data-testid="admin-uploads-direct" />
          <span>
            <b>Subida directa al almacenamiento</b>
            <small>Los archivos de más de 8 MB van del navegador al almacenamiento sin pasar por el
              servidor. Sin ella, el máximo real es {platform.multipart_max_mb} MB aunque el plan
              permita más. Necesita el CORS del bucket habilitado para PUT.</small>
          </span>
        </label>

        <p className="sml-adu-note">
          Tope de la plataforma: {platform.ceiling_mb} MB por archivo (variable UPLOAD_MAX_MB_CEILING).
          Si borrás un plan, sus usuarios pasan al plan por defecto.
        </p>
      </div>
    </div>
  );
}
